import { Routes, Route, Navigate, useParams } from "react-router-dom";

import Home from "../pages/Home";
import NotFound from "../pages/NotFound";
import LoginForm from "../pages/auth/pages/login";
import Welcome from "../pages/auth/pages/welcome";
import RegisterForm from "../pages/auth/pages/register";
import AssistantRegister from "../pages/auth/pages/assistant_register";
import PasswordUpdateForm from "../pages/auth/pages/forget_pass";
import VerifyEmail from "../pages/auth/pages/verify_email";
import ResetPassword from "../pages/auth/pages/reset_password";
import AuthCallback from "../pages/auth/pages/auth_callback";

import AssistantKyc from "../pages/assistant/Kyc";
import AssistantExperience from "../pages/assistant/Experience";
import DocumentVerification from "../pages/assistant/DocumentVerification";
import VehicleInformation from "../pages/assistant/VehicleInformation";
import PricingAccount from "../pages/assistant/PricingAccount";

import HomeTravelAssistant from "../pages/HomeTravelAssistant";
import HomeRides from "../pages/HomeRides";
import HomeStays from "../pages/HomeStays";
import StayDetails from "../pages/StayDetails";

import ProfileManagement from "../pages/ProfileManagement";
import About from "../pages/About";
import Contact from "../pages/Contact";
import Blog from "../pages/Blog";
import Explore from "../pages/Explore";
import HelpCenter from "../pages/HelpCenter";
import Terms from "../pages/Terms";
import Privacy from "../pages/Privacy";

import AdminRoute from "./AdminRoute";
import { AuthProvider } from "./AuthProvider";
import MaintenanceGuard from "./MaintenanceGuard";
import Maintenance from "../pages/Maintenance";
import ServiceGuard from "./ServiceGuard";
import PublicRoute from "./PublicRoute";
import RoleRoute from "./RoleRoute";

// ================= USER DASHBOARD =================
import TravelAssistant from "../pages/dashboards/TravelAssistant";
import UserMessages from "../pages/dashboards/UserMessages";
import Rides from "../pages/dashboards/Rides";
import Stay from "../pages/dashboards/Stays";
import UserContact from "../pages/dashboards/UserContact";
import MyTrips from "../pages/dashboards/MyTrips";
import TripDetails from "../pages/dashboards/TripDetails";

// ================= ASSISTANT DASHBOARD =================
import AssistantDashboardLayout from "../pages/dashboards/assistant/AssistantDashboardLayout";
import Overview from "../pages/dashboards/assistant/Overview";
import Bookings from "../pages/dashboards/assistant/Bookings";
import Settings from "../pages/dashboards/assistant/Settings";
import Tours from "../pages/dashboards/assistant/Tours";
import ProfileAssistant from "../pages/dashboards/assistant/ProfileAssistant";
import AssistantProfileDashboard from "../pages/assistant/AssistantProfile";
import Messages from "../pages/dashboards/assistant/Messages";

// ================= ADMIN =================
import AdminDashboard from "../pages/admin/AdminDashboard";
import AdminAssistants from "../pages/admin/AdminAssistants";
import AdminFinances from "../pages/admin/AdminFinances";
import AdminWithdrawals from "../pages/admin/AdminWithdrawals";
import AdminRefunds from "../pages/admin/AdminRefunds";
import AdminSettings from "../pages/admin/AdminSettings";
import AdminAssistantDetail from "../pages/admin/AdminAssistantDetail";
import AdminUsers from "../pages/admin/AdminUsers";
import AdminUserDetail from "../pages/admin/AdminUserDetail";

function AcceptOrRejectRequestRedirect() {
  const { requestId } = useParams();
  return (
    <Navigate
      to={`/dashboards/assistant/bookings${requestId ? `?booking=${requestId}` : ""}`}
      replace
    />
  );
}

function Pages() {
  return (
    <MaintenanceGuard>
      <Routes>
        {/* ================= MAIN WEBSITE (PUBLIC) ================= */}
        <Route
          path="/"
          element={
            <PublicRoute>
              <Home />
            </PublicRoute>
          }
        />

        {/* ================= MAINTENANCE SCREEN ================= */}
        <Route path="/maintenance" element={<Maintenance />} />

        <Route path="*" element={<NotFound />} />

        <Route path="/auth/welcome" element={<Welcome />} />
        <Route path="/auth/login" element={<LoginForm />} />
        <Route path="/auth/admin/login" element={<LoginForm isAdmin />} />
        <Route path="/auth/register" element={<RegisterForm />} />
        <Route path="/auth/assistant-register" element={<AssistantRegister />} />
        <Route path="/auth/update-password" element={<PasswordUpdateForm />} />
        <Route path="/auth/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        {/* Where the API sends the browser after a social sign-in handshake. */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        <Route path="/about" element={<About />} />
        <Route
          path="/contact"
          element={
            <PublicRoute authenticatedPath="/user/contact">
              <Contact />
            </PublicRoute>
          }
        />
        <Route path="/blog" element={<Blog />} />
        <Route path="/help-center" element={<HelpCenter />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />

        <Route
          path="/travel-assistant"
          element={
            <PublicRoute authenticatedPath="/user/travel-assistant">
              <ServiceGuard service="puloAssistants">
                <HomeTravelAssistant />
              </ServiceGuard>
            </PublicRoute>
          }
        />
        <Route
          path="/rides"
          element={
            <PublicRoute authenticatedPath="/user/rides">
              <ServiceGuard service="rides">
                <HomeRides />
              </ServiceGuard>
            </PublicRoute>
          }
        />
        <Route
          path="/stays"
          element={
            <PublicRoute authenticatedPath="/user/stays">
              <ServiceGuard service="stays">
                <HomeStays />
              </ServiceGuard>
            </PublicRoute>
          }
        />
        <Route
          path="/stays/:id"
          element={
            <PublicRoute authenticatedPath="/user/stays">
              <ServiceGuard service="stays">
                <StayDetails />
              </ServiceGuard>
            </PublicRoute>
          }
        />
        <Route path="/explore/:city?" element={<Explore />} />
        <Route path="/assistant/:id" element={<AssistantProfileDashboard />} />
        <Route path="/messages" element={<UserMessages />} />

        {/* ================= ALL PROTECTED ROUTES ================= */}
        <Route element={<AuthProvider />}>

          <Route element={<RoleRoute allow="assistant" />}>
            {/* ================= ASSISTANT ONBOARDING ================= */}
            <Route path="/assistant/kyc" element={<AssistantKyc />} />
            <Route path="/assistant/experience" element={<AssistantExperience />} />
            <Route path="/assistant/documents" element={<DocumentVerification />} />
            <Route path="/assistant/vehicle" element={<VehicleInformation />} />
            <Route path="/assistant/pricing" element={<PricingAccount />} />

            {/* ================= ASSISTANT DASHBOARD ================= */}
            <Route path="/dashboards/assistant" element={<AssistantDashboardLayout />}>
              <Route index element={<Overview />} />
              <Route path="overview" element={<Overview />} />
              <Route path="bookings" element={<Bookings />} />
              <Route path="settings" element={<Settings />} />
              <Route path="settings/:section" element={<Settings />} />
              <Route path="tours" element={<Tours />} />
              <Route path="profile" element={<ProfileAssistant />} />
              {/* Legacy target of the "new booking request" email. It never
                  rendered accept/reject controls — send it to the bookings
                  list with the request open so links already in inboxes work. */}
              <Route
                path="profile/acceptOrRejectRequest/:requestId"
                element={<AcceptOrRejectRequestRedirect />}
              />
              <Route path="messages" element={<Messages />} />
            </Route>

            <Route
              path="/assistant/assistant-profile"
              element={<Navigate to="/dashboards/assistant/profile" replace />}
            />
            <Route path="/assistant/profile" element={<ProfileManagement />} />
          </Route>

          {/* ================= USER DASHBOARD ================= */}
          <Route element={<RoleRoute allow="user" />}>
            <Route
              path="/user/travel-assistant"
              element={
                <ServiceGuard service="puloAssistants">
                  <TravelAssistant />
                </ServiceGuard>
              }
            />
            <Route
              path="/user/rides"
              element={
                <ServiceGuard service="rides">
                  <Rides />
                </ServiceGuard>
              }
            />
            <Route
              path="/user/stays"
              element={
                <ServiceGuard service="stays">
                  <Stay />
                </ServiceGuard>
              }
            />
            <Route path="/user/contact" element={<UserContact />} />
            <Route path="/user/messages" element={<UserMessages />} />
            <Route path="/user/trips" element={<MyTrips />} />
            <Route path="/user/trips/:bookingId" element={<TripDetails />} />
            <Route path="/user/profile" element={<ProfileManagement />} />
          </Route>

          {/* ================= ADMIN ================= */}
          <Route path="/admin" element={
            <AdminRoute><AdminDashboard /></AdminRoute>
          } />
          <Route path="/admin/assistants" element={
            <AdminRoute><AdminAssistants /></AdminRoute>
          } />
          <Route path="/admin/assistants/:id" element={
            <AdminRoute><AdminAssistantDetail /></AdminRoute>
          } />
          <Route path="/admin/users" element={
            <AdminRoute><AdminUsers /></AdminRoute>
          } />
          <Route path="/admin/users/:id" element={
            <AdminRoute><AdminUserDetail /></AdminRoute>
          } />
          <Route path="/admin/finances" element={
            <AdminRoute><AdminFinances /></AdminRoute>
          } />
          <Route path="/admin/withdrawals" element={
            <AdminRoute><AdminWithdrawals /></AdminRoute>
          } />
          <Route path="/admin/refunds" element={
            <AdminRoute><AdminRefunds /></AdminRoute>
          } />
          <Route path="/admin/settings" element={
            <AdminRoute><AdminSettings /></AdminRoute>
          } />

        </Route>
      </Routes>
    </MaintenanceGuard>
  );
}

export default Pages;
